import { useCallback, useEffect, useState } from 'react'
import { Button, Modal, Select } from 'antd'
import { useHistory } from 'react-router-dom'
import { API } from 'src/utils/interceptor'
import VenueGroupRunDialog from 'src/screens/venue-groups/VenueGroupRunDialog'
import {
  groupMissesPath,
  shareResultsHaveMisses,
} from 'src/screens/venue-groups/runReceipt'
import { SuccessNotification } from 'src/notifications/SuccessNotification'

/**
 * What a saved change can be pushed as. Future screens pass a kind from this map.
 * The run still skips a venue that has its own later edits.
 */
export const shareChangeRun = offer => {
  if (offer?.kind === 'recipe' && offer.recipeId) {
    return {
      action: 'share_recipes',
      payload: {
        recipeId: offer.recipeId,
        ...(offer.venueId ? { sourceVenueId: offer.venueId } : {}),
      },
    }
  }
  if (offer?.kind === 'menu' && offer.menuId) {
    return { action: 'copy_menu', payload: { menuId: offer.menuId } }
  }
  if (offer?.kind === 'categories' && offer.venueId) {
    return {
      action: 'copy_recipe_categories',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  if (offer?.kind === 'allergens' && offer.venueId) {
    return {
      action: 'copy_allergen_flags',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  if (offer?.kind === 'standingOrders' && offer.venueId) {
    return {
      action: 'copy_standing_orders',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  if (offer?.kind === 'connectSupplier' && offer.venueId && offer.supplierId) {
    return {
      action: 'connect_supplier',
      payload: { sourceVenueId: offer.venueId, supplierId: offer.supplierId },
    }
  }
  if (offer?.kind === 'priceFiles' && offer.venueId && offer.supplierId) {
    return {
      action: 'point_price_files',
      payload: { sourceVenueId: offer.venueId, supplierId: offer.supplierId },
    }
  }
  if (offer?.kind === 'businessDays' && offer.venueId) {
    return {
      action: 'copy_business_days',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  if (offer?.kind === 'stockCountSheet' && offer.groupId) {
    return {
      action: 'copy_stock_count_sheet',
      payload: { groupId: offer.groupId },
    }
  }
  if (offer?.kind === 'homeTask' && offer.itemId && offer.role) {
    return {
      action: 'copy_home_task',
      payload: { itemId: offer.itemId, role: offer.role },
    }
  }
  if (offer?.kind === 'shoppingList' && offer.venueId) {
    return {
      action: 'apply_shopping_list',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  if (offer?.kind === 'restrictedSuppliers' && offer.venueId) {
    return {
      action: 'copy_restricted_suppliers',
      payload: { sourceVenueId: offer.venueId },
    }
  }
  return null
}

/**
 * Groups that still have somewhere else to land after the source venue is left out.
 * A one-venue group (or one where you only manage the source) is not offered.
 */
export const groupsYouCanShareInto = (groups, sourceVenueId) => {
  const source = String(sourceVenueId || '')
  return (groups || []).filter(group => {
    const ids = (group.venues || []).map(id => String(id || '')).filter(Boolean)
    if (ids.length > 0) {
      return ids.some(id => id !== source)
    }
    return Number(group.venueCount) > 1
  })
}

const SHARE_GROUP_LAST_USED_KEY = 'oms.venueGroup.shareLastUsed'

export const groupShareTargetCount = group => {
  const ids = (group?.venues || []).map(id => String(id || '')).filter(Boolean)
  if (ids.length) return ids.length
  return Number(group?.venueCount) || 0
}

export const groupIncludesVenue = (group, venueId) => {
  const source = String(venueId || '')
  if (!source) return false
  const ids = (group?.venues || []).map(id => String(id || '')).filter(Boolean)
  if (ids.length) return ids.includes(source)
  return Number(group?.venueCount) > 0
}

/** Last used for this venue, else the largest multi-venue group that includes it. */
export const pickDefaultShareGroup = (
  groups,
  { sourceVenueId, lastUsedId } = {},
) => {
  const list = groups || []
  if (!list.length) return null
  const last = String(lastUsedId || '')
  if (last) {
    const remembered = list.find(group => String(group._id) === last)
    if (remembered) return remembered
  }
  const withVenue = list.filter(group =>
    groupIncludesVenue(group, sourceVenueId),
  )
  const pool = withVenue.length ? withVenue : list
  return pool.reduce((best, group) => {
    if (!best) return group
    return groupShareTargetCount(group) > groupShareTargetCount(best)
      ? group
      : best
  }, null)
}

export const readShareGroupLastUsed = venueId => {
  try {
    const raw = window.localStorage.getItem(SHARE_GROUP_LAST_USED_KEY)
    const map = raw ? JSON.parse(raw) : {}
    return map[String(venueId || '')] || null
  } catch (error) {
    return null
  }
}

export const writeShareGroupLastUsed = (venueId, groupId) => {
  try {
    const key = String(venueId || '')
    const id = String(groupId || '')
    if (!key || !id) return
    const raw = window.localStorage.getItem(SHARE_GROUP_LAST_USED_KEY)
    const map = raw ? JSON.parse(raw) : {}
    map[key] = id
    window.localStorage.setItem(SHARE_GROUP_LAST_USED_KEY, JSON.stringify(map))
  } catch (error) {
    // Private mode / blocked storage — keep the in-session pick only.
  }
}

export const SHARE_NO_GROUP = 'This venue is not in a group.'
export const SHARE_NO_OTHER_VENUES =
  'This venue’s groups have no other venues you can update. Add another venue to a group first.'
export const SHARE_GROUPS_LOAD_FAILED =
  'Could not check which groups this venue is in.'

const ShareChangeWithGroup = ({ offer, onClose }) => {
  const history = useHistory()
  const [groups, setGroups] = useState([])
  const [groupId, setGroupId] = useState(null)
  const [open, setOpen] = useState(false)
  const [empty, setEmpty] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const closeReview = useCallback(() => setReviewing(false), [])

  const afterShareFinished = note => {
    if (!shareResultsHaveMisses(note) || !groupId) return
    const toastKey = 'share-with-group-view-misses'
    const path = groupMissesPath(groupId)
    SuccessNotification({
      message: 'Some venues did not take this share.',
      duration: 8,
      key: toastKey,
      btn: (
        <Button
          size='small'
          type='primary'
          onClick={() => {
            closeReview()
            onClose()
            history.push(path)
          }}
        >
          View misses
        </Button>
      ),
    })
  }

  useEffect(() => {
    if (!offer?.venueId || !shareChangeRun(offer)) return undefined
    let cancelled = false
    API.get('api2', `venue-groups?venueId=${offer.venueId}`)
      .then(response => {
        if (cancelled) return null
        const rows = response?.data || []
        const shareable = groupsYouCanShareInto(rows, offer.venueId)
        if (!rows.length) {
          setEmpty(SHARE_NO_GROUP)
          setGroups([])
          setGroupId(null)
          setOpen(true)
          return null
        }
        if (!shareable.length) {
          setEmpty(SHARE_NO_OTHER_VENUES)
          setGroups([])
          setGroupId(null)
          setOpen(true)
          return null
        }
        const picked = pickDefaultShareGroup(shareable, {
          sourceVenueId: offer.venueId,
          lastUsedId: readShareGroupLastUsed(offer.venueId),
        })
        setEmpty('')
        setGroups(shareable)
        setGroupId(picked?._id || shareable[0]._id)
        setOpen(true)
        return null
      })
      .catch(() => {
        if (cancelled) return null
        setEmpty(SHARE_GROUPS_LOAD_FAILED)
        setGroups([])
        setGroupId(null)
        setOpen(true)
        return null
      })
    return () => {
      cancelled = true
    }
  }, [offer, onClose])

  const planned = shareChangeRun(offer)

  return (
    <>
      <Modal
        visible={open}
        title={empty ? 'Share with group' : 'Share this change with a group?'}
        okText={empty ? 'Close' : 'Review'}
        cancelText='Not now'
        cancelButtonProps={empty ? { style: { display: 'none' } } : undefined}
        onOk={() => {
          if (empty) {
            onClose()
            return
          }
          writeShareGroupLastUsed(offer?.venueId, groupId)
          setReviewing(true)
        }}
        onCancel={onClose}
      >
        {empty ? (
          <p>{empty}</p>
        ) : (
          <>
            <p>
              This change stays on this venue until you share it. Venues that
              have edited their own copy keep that edit.
            </p>
            <Select
              style={{ width: '100%' }}
              value={groupId}
              onChange={setGroupId}
            >
              {groups.map(group => (
                <Select.Option key={group._id} value={group._id}>
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </>
        )}
      </Modal>
      <VenueGroupRunDialog
        open={reviewing}
        groupId={groupId}
        action={planned?.action}
        payload={planned?.payload}
        onFinished={afterShareFinished}
        onClose={() => {
          closeReview()
          onClose()
        }}
      />
    </>
  )
}

export default ShareChangeWithGroup
